import { useEffect, useState } from 'react';
import { Search } from '@vitness/icons';
import { CommandPalette, type CommandItem } from './CommandPalette.js';

export interface TopBarSearchProps {
  /** `box` is the full desktop field; `icon` is the compact mobile button. */
  variant?: 'box' | 'icon';
  placeholder?: string;
  /** The search corpus (products, screens, quick actions) supplied by the host. */
  commands?: CommandItem[];
  /** Bind the ⌘K / Ctrl+K global shortcut to open the palette. Defaults to true. */
  shortcut?: boolean;
}

/**
 * Universal top-bar search. A single, always-present control across every
 * platform/product top bar: on desktop it's the wide command-palette trigger
 * (search icon + placeholder + ⌘K hint); on mobile it collapses to an icon
 * button so it stays exposed without crowding the bar. Both open the shared
 * {@link CommandPalette}. The control owns its open state and the ⌘K shortcut.
 */
export function TopBarSearch({
  variant = 'box',
  placeholder = 'Search products, docs, endpoints…',
  commands,
  shortcut = true,
}: TopBarSearchProps) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!shortcut) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && (event.key === 'k' || event.key === 'K')) {
        event.preventDefault();
        setOpen((current) => !current);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [shortcut]);

  const palette = (
    <CommandPalette
      open={open}
      onClose={() => setOpen(false)}
      placeholder={placeholder}
      commands={commands}
    />
  );

  if (variant === 'icon') {
    return (
      <>
        <button
          type="button"
          aria-label="Search"
          onClick={() => setOpen(true)}
          className="vn-tb-ghost"
          style={{
            width: 34,
            height: 34,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: 'none',
            background: 'transparent',
            color: 'var(--vn-text-secondary)',
            borderRadius: 'var(--vn-radius-md)',
            cursor: 'pointer',
          }}
        >
          <Search width={18} height={18} stroke="currentColor" strokeWidth={1.8} />
        </button>
        {palette}
      </>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="vn-tb-soft"
        style={{
          flex: '0 0 auto',
          maxWidth: '100%',
          height: 32,
          display: 'flex',
          alignItems: 'center',
          gap: 9,
          padding: '0 12px',
          borderRadius: 'var(--vn-radius-md)',
          border: '1px solid var(--vn-border-default)',
          background: 'var(--vn-surface-input)',
          color: 'var(--vn-text-muted)',
          cursor: 'text',
        }}
      >
        <Search
          width={14}
          height={14}
          stroke="currentColor"
          strokeWidth={1.7}
          style={{ flexShrink: 0 }}
        />
        <span
          style={{
            fontSize: 12,
            textAlign: 'left',
            whiteSpace: 'nowrap',
          }}
        >
          {placeholder}
        </span>
        <kbd
          style={{
            fontFamily: 'var(--vn-font-mono)',
            fontSize: 9,
            border: '1px solid var(--vn-border-strong)',
            borderRadius: 'var(--vn-radius-xs)',
            padding: '1px 5px',
            color: 'var(--vn-text-secondary)',
            flexShrink: 0,
          }}
        >
          ⌘K
        </kbd>
      </button>
      {palette}
    </>
  );
}

export default TopBarSearch;
